import React, { useMemo } from "react";
import { Button } from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, parseISO } from "date-fns";

const DATE_FORMAT = "yyyy-MM-dd";

/**
 * Valuation-date lookup for the Risk Limits page — lets a user pull
 * exposure/utilization for any historical date with data, not just the
 * latest. Mirrors src/pages/pricing/components/PriceDateSelector.js's
 * pattern (same MUI x-date-pickers setup, same "only known dates are
 * selectable" behavior via DateContext's `dates` list).
 */
export default function RiskLimitsDatePicker({ value, latestDate, availableDates = [], onChange }) {
  const { availableSet, minDate, maxDate } = useMemo(() => {
    const sorted = [...availableDates].sort();
    return {
      availableSet: new Set(availableDates),
      minDate: sorted.length ? parseISO(sorted[0]) : undefined,
      maxDate: sorted.length ? parseISO(sorted[sorted.length - 1]) : undefined,
    };
  }, [availableDates]);

  // No explicit pick means the page is showing the latest date, so the field shows that date rather than staying
  // empty (the "Latest" button below still resets to it).
  const shownDate = value || latestDate;
  const dateValue = shownDate ? parseISO(shownDate) : null;
  const isLatest = !value || value === latestDate;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DatePicker
        label="Valuation Date"
        value={dateValue}
        minDate={minDate}
        maxDate={maxDate}
        shouldDisableDate={(d) => availableSet.size > 0 && !availableSet.has(format(d, DATE_FORMAT))}
        onChange={(d) => {
          if (d && !Number.isNaN(d.getTime())) {
            onChange(format(d, DATE_FORMAT));
          }
        }}
        slotProps={{ textField: { size: "small", sx: { minWidth: 180 } } }}
      />
      <Button size="small" onClick={() => onChange(null)} disabled={isLatest} sx={{ ml: 1 }}>
        Latest
      </Button>
    </LocalizationProvider>
  );
}
